import React, { useState, useEffect } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import * as Yup from "yup";
import { useFormik } from "formik";
import { errorToast, successToast } from "../../helpers";
import { UploadMedia } from "../../utils/uploadMedia";
import Loader from "../../helpers/Loader";
import { Close } from "@mui/icons-material";
import {
  useLazyGetFormsByIdQuery,
  useUpdateFormsMultiMutation,
  useUpdateFormsMutation,
} from "../../services/forum";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import {
  BULK_STATUS,
  CONTENT_TYPE,
  LISTING,
} from "../../helpers/constants/enums";
import {
  useGetCatSubListQuery,
  useLazyGetCatSubListQuery,
} from "../../services/category";
import EditText from "../../components/EditText";

const AddForum = () => {
  const navigate = useNavigate();
  const { _id } = useParams();
  const [selectedTutor, setSelectedTutor] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [error, setError] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [forumData] = useLazyGetFormsByIdQuery();
  // const [updateForum] = useUpdateFormsMutation();
  const [updateForum] = useUpdateFormsMultiMutation();
  const [initialValuesSet, setInitialValuesSet] = useState(false);
  const [getAllSubject] = useLazyGetCatSubListQuery();
  const [subjectData, setSubjectData] = useState<any[]>([]);
  const { data: categoryData } = useGetCatSubListQuery({
    type: LISTING.CATEGORY,
  });

  const MAX_IMAGES = 10;

  const formik = useFormik({
    initialValues: {
      images: [] as string[],
      username: "",
      name: "",
      title: "",
      description: "",
      category: "",
      subject: [] as string[], // Changed to array
      isAnonymous: false,
    },
    validationSchema: Yup.object().shape({
      // username: Yup.string().required("Username is required"),
      // name: Yup.string().required("Name is required"),
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
      images: Yup.array()
        .min(1, "At least one image is required")
        .max(MAX_IMAGES, `Maximum ${MAX_IMAGES} images allowed`),
      subject: Yup.array().min(1, "At least one subject is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        setError(true);
        return;
      }
      setSubmitting(true);

      const body = {
        title: values.title,
        description: values.description,
        images: values.images,
        contentType: CONTENT_TYPE.FORUM,
        categoryId: values.category,
        subjectId: values.subject,
        isAnonymous: Boolean(values.isAnonymous),
        type: BULK_STATUS.UPDATE,
        ids: [_id],
      };

      // return;
      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response: any = await updateForum({
          body: encryptedBody,
        }).unwrap();
        if (response?.statusCode === 200) {
          successToast("Forum updated successfully");
          navigate("/manage-forum");
        }
      } catch (error: any) {
        console.error("Error adding class:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchSubjects = async () => {
    if (!formik.values.category) return;

    try {
      const response = await getAllSubject({
        type: LISTING.SUBJECT,
        categoryId: formik.values.category,
      }).unwrap();

      if (response.statusCode === 200) {
        setSubjectData(response?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (formik.values.category) {
      fetchSubjects();
    }
  }, [formik.values.category]);

  const fetchForumData = async () => {
    try {
      const response = await forumData({ id: _id }).unwrap();
      if (response.statusCode === 200) {
        const data = response.data;
        setImages(data.images || []);
        setSelectedTutor(data.user?._id || "");
        setSelectedSubject(data.subjects?.name || "");

        // Handle subjects array from API
        const initialSubjects =
          data.subjects?.map((subj: any) => subj._id) || [];

        formik.setValues({
          images: data.images || [],
          username: data.user.userName || "",
          name: data.user.name || "",
          title: data.title || "",
          description: data.description || "",
          // subject: data.subjects?.name || "",
          category: data.category?._id,
          subject: initialSubjects,
          isAnonymous: data.isAnonymous,
        });
        setInitialValuesSet(true);
      }
    } catch (error) {
      console.error(error);
      errorToast("Failed to load forum data");
    }
  };

  useEffect(() => {
    if (_id) {
      fetchForumData();
    } else {
      setInitialValuesSet(true);
    }
  }, [_id]);

  const handleTutorChange = (event: any) => {
    setSelectedTutor(event.target.value);
    formik.setFieldValue("tutor", event.target.value);
  };

  const handleSubjectChange = (event: any) => {
    setSelectedSubject(event.target.value);
    formik.setFieldValue("subject", event.target.value);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (images.length + files.length > MAX_IMAGES) {
      errorToast(`You can upload a maximum of ${MAX_IMAGES} images`);
      return;
    }

    setIsUploading(true);
    try {
      const newImages = [...images];
      const filesToProcess = Array.from(files).slice(
        0,
        MAX_IMAGES - images.length
      );

      for (const file of filesToProcess) {
        const res = await UploadMedia(file);
        if (res?.statusCode === 200) {
          newImages.push(res?.data?.image);
        } else {
          errorToast(res?.message || "Error uploading image");
        }
      }

      setImages(newImages);
      formik.setFieldValue("images", newImages);
    } catch (error) {
      console.error("Error uploading images:", error);
      errorToast("Error uploading images. Please try again.");
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
    formik.setFieldValue("images", newImages);
  };

  if (!initialValuesSet) {
    return <Loader isLoad={true} />;
  }

  return (
    <MainContainer>
      <div className="main_loyout">
        <Card className="cards">
          <div className="dashboard">
            <h1 className="mn_hdng">{_id ? "Edit Forum" : "Add Forum"}</h1>
            <Loader isLoad={formik.isSubmitting || isUploading} />
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/manage-forum")}
            >
              Back
            </Button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography className="custom_label">Images</Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {images.map((image, index) => (
                      <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                        <Box sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={image}
                            alt={`forum-image-${index}`}
                            sx={{
                              height: 150,
                              width: "100%",
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                          <IconButton
                            sx={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.7)",
                              },
                            }}
                            onClick={() => handleRemoveImage(index)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {images.length < MAX_IMAGES && (
                    <Box className="upload_image">
                      <label htmlFor="icon-button-file">
                        <input
                          style={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleImageUpload}
                          multiple
                          disabled={isUploading}
                        />
                        <Button component="span" className="upload_image_btn">
                          <img
                            style={{
                              height: 150,
                              width: 370,
                              borderRadius: 1,
                            }}
                            src="/static/images/document.png"
                            alt=""
                          />
                          <CameraAltIcon />
                        </Button>
                      </label>
                    </Box>
                  )}
                  {error && formik.touched.images && formik.errors.images && (
                    <Typography color="error" variant="caption">
                      {formik.errors.images}
                    </Typography>
                  )}
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Typography className="custom_label">Username</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="username"
                    variant="outlined"
                    fullWidth
                    placeholder="Username"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.username}
                    helperText={
                      formik.touched.username && formik.errors.username
                        ? formik.errors.username.toString()
                        : ""
                    }
                    disabled={Boolean(_id)}
                  />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Typography className="custom_label">Name</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="name"
                    variant="outlined"
                    fullWidth
                    placeholder="Name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    helperText={
                      formik.touched.name && formik.errors.name
                        ? formik.errors.name.toString()
                        : ""
                    }
                    disabled={Boolean(_id)}
                  />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FormControl fullWidth>
                    <Typography className="custom_label">Category</Typography>
                    <Select
                      className="select_div"
                      id="category"
                      name="category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Category
                      </MenuItem>
                      {categoryData?.data?.map((item: any) => (
                        <MenuItem key={item._id} value={item._id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.category && formik.errors.category && (
                      <h6 className="err_msg">{formik.errors.category}</h6>
                    )}
                  </FormControl>
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FormControl fullWidth>
                    <Typography className="custom_label">Subjects*</Typography>
                    <Select
                      className="select_div"
                      id="subject"
                      name="subject"
                      multiple
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                      disabled={!formik.values.category} // Disable if no category selected
                      // renderValue={(selected) => {
                      //   if ((selected as string[]).length === 0) {
                      //     return <em>Select subjects</em>;
                      //   }
                      //   return (
                      //     <Box
                      //       sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                      //     >
                      //       {(selected as string[]).map((value) => {
                      //         const selectedSubject = subjectData.find(
                      //           (subj) => subj._id === value
                      //         );
                      //         return (
                      //           <Chip
                      //             key={value}
                      //             label={selectedSubject?.name || value}
                      //             onDelete={(e) => {
                      //               e.stopPropagation(); // Prevent menu from closing
                      //               const newSubjects =
                      //                 formik.values.subject.filter(
                      //                   (subj) => subj !== value
                      //                 );
                      //               formik.setFieldValue(
                      //                 "subject",
                      //                 newSubjects
                      //               );
                      //             }}
                      //             deleteIcon={<Close fontSize="small" />}
                      //           />
                      //         );
                      //       })}
                      //     </Box>
                      //   );
                      // }}
                      // Update the renderValue prop in your Select component:
                      renderValue={(selected) => {
                        if ((selected as string[]).length === 0) {
                          return <em>Select subjects</em>;
                        }
                        return (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((value) => {
                              const selectedSubject = subjectData.find(
                                (subj) => subj._id === value
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={selectedSubject?.name || value}
                                  onDelete={(e) => {
                                    e.preventDefault(); // Stop event propagation
                                    e.stopPropagation(); // Prevent menu from opening
                                    const newSubjects =
                                      formik.values.subject.filter(
                                        (subj) => subj !== value
                                      );
                                    formik.setFieldValue(
                                      "subject",
                                      newSubjects
                                    );
                                  }}
                                  deleteIcon={<Close fontSize="small" />}
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                    >
                      <MenuItem value="" disabled>
                        {formik.values.category
                          ? "Select subjects"
                          : "Select a category first"}
                      </MenuItem>
                      {subjectData?.map((item: any) => (
                        <MenuItem key={item._id} value={item._id}>
                          <Checkbox
                            checked={formik.values.subject.includes(item._id)}
                          />
                          <ListItemText primary={item.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.subject && formik.errors.subject && (
                      <Typography color="error" variant="caption">
                        {formik.errors.subject}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Typography className="custom_label">Title</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="title"
                    variant="outlined"
                    fullWidth
                    placeholder="Title"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length === 1 && value === " ") return;
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                    helperText={
                      formik.touched.title && formik.errors.title
                        ? formik.errors.title.toString()
                        : ""
                    }
                  />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.isAnonymous}
                        onChange={(e) =>
                          formik.setFieldValue("isAnonymous", e.target.checked)
                        }
                        name="isAnonymous"
                        color="primary"
                      />
                    }
                    label="Allow Comments"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography className="custom_label">Description</Typography>
                  {/* <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="description"
                    multiline
                    minRows={4}
                    maxRows={6}
                    variant="outlined"
                    fullWidth
                    placeholder="Description"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length === 1 && value === " ") return;
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.description}
                    helperText={
                      formik.touched.description && formik.errors.description
                        ? formik.errors.description.toString()
                        : ""
                    }
                  /> */}
                  {/* <Typography className="custom_label">description</Typography> */}
                  {_id ? (
                    formik.values.description && (
                      <EditText
                        content={formik.values.description}
                        setContent={(value) => {
                          if (value.length === 1 && value === " ") return; // Prevent leading space
                          formik.setFieldValue("description", value); // Update Formik
                        }}
                      />
                    )
                  ) : (
                    <EditText
                      content={formik.values.description}
                      setContent={(value) =>
                        formik.setFieldValue("description", value)
                      }
                    />
                  )}
                </Grid>
              </Grid>
              <div className="form_btn">
                <Button
                  size="large"
                  type="submit"
                  className="btn btn_primary"
                  onClick={() => setError(true)}
                  disabled={isUploading}
                >
                  {_id ? "Update" : "Submit"}
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </MainContainer>
  );
};

export default AddForum;
